import * as React from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { AlertTitle } from "@mui/material";
import {
  clearProductInterval,
  setProductInterval,
} from "@/utils/productTimers";

const DEFAULT_DURATION_MS = 3000;
// Breathing room between an anchored toast and the element it sits above —
// enough that it doesn't sit on top of the last message.
const ANCHOR_GAP_PX = 20;
// An anchor below this fraction of the window is treated as bottom-pinned (a
// composer under a conversation), and the toast is placed above it. Higher than
// that — an empty chat, where the composer sits mid-screen — and hovering the
// toast just above it would leave it floating in the middle of the page, so it
// goes to the top of the window instead and only borrows the anchor's centre.
const ANCHOR_IS_BOTTOM_PINNED = 0.5;
// The anchor often isn't in the DOM yet when the toast opens (on a refresh the
// composer renders after the conversation loads), and it moves as the composer
// grows. Re-measuring on a timer is what stops the toast being stranded at the
// top of the window for the rest of the page's life.
const ANCHOR_REMEASURE_MS = 400;

const FpToast = () => {
  const [open, setOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastSeverity, setToastSeverity] = React.useState("");
  const [toastDuration, setToastDuration] = React.useState(DEFAULT_DURATION_MS);
  const [toastTitle, setToastTitle] = React.useState("");
  const [vertical, setVertical] = React.useState("top");
  const [horizontal, setHorizontal] = React.useState("center");
  // CSS selector of an element to centre the toast over, instead of the
  // viewport. A viewport-centred toast reads as off to the left of a composer
  // that is itself offset by the sidebar (#11221).
  const [anchorSelector, setAnchorSelector] = React.useState(null);
  // Where the anchor sits, so the toast can be placed directly above it rather
  // than at the top of the window.
  const [anchorRect, setAnchorRect] = React.useState(null);
  // Set per-toast by `openFpToast`; see the comment where it's assigned.
  const dismissHandler = React.useRef(null);

  const openSnackbar = (toastMessage, toastSeverity, options) => {
    const { title, duration, vertical, horizontal, anchorSelector, onDismiss } =
      options || {};
    // Kept in a ref rather than state: it must not trigger a re-render, and the
    // handler that fires it is created once. A caller that wants to know the
    // user dismissed it by hand — as opposed to it being replaced or retracted
    // — needs this; there is otherwise no way to tell the two apart, and the
    // subscription warning has to stop re-appearing once acknowledged (#11221).
    dismissHandler.current = onDismiss || null;
    setAnchorSelector(anchorSelector || null);
    if (vertical) setVertical(vertical);
    if (horizontal) setHorizontal(horizontal);
    if (title) setToastTitle(title);
    else setToastTitle("");
    // `null` means "stay until dismissed" (MUI reads that as no auto-hide).
    // Distinguished from an omitted duration, which keeps the 3s default —
    // testing `if (duration)` treated the two the same and made a persistent
    // toast impossible to ask for (#11221).
    setToastDuration(duration === undefined ? DEFAULT_DURATION_MS : duration);
    setToastMessage(toastMessage);
    setToastSeverity(toastSeverity);
    setOpen(true);
  };

  React.useEffect(() => {
    window.openFpToast = openSnackbar;
    // Lets a caller retract its own toast once it's no longer relevant — the
    // subscription warning dismisses itself when the user sends anyway (#11221)
    // rather than sitting there for the rest of the conversation.
    window.closeFpToast = () => setOpen(false);
  }, []);

  // Measured while the toast is up, and re-measured on resize: the sidebar can
  // collapse underneath it, which moves the composer without remounting us.
  React.useEffect(() => {
    if (!open || !anchorSelector) {
      setAnchorRect(null);
      return undefined;
    }
    const measure = () => {
      const el = document.querySelector(anchorSelector);
      if (!el) {
        // Anchor absent (not rendered yet, or not on this surface) — fall back
        // to the plain viewport placement rather than pinning to a stale spot.
        setAnchorRect((prev) => (prev === null ? prev : null));
        return;
      }
      const rect = el.getBoundingClientRect();
      const next = {
        centerX: rect.left + rect.width / 2,
        top: rect.top,
        above: rect.top > window.innerHeight * ANCHOR_IS_BOTTOM_PINNED,
      };
      // Only re-render when something actually moved; this runs on a timer.
      setAnchorRect((prev) =>
        prev &&
        prev.centerX === next.centerX &&
        prev.top === next.top &&
        prev.above === next.above
          ? prev
          : next
      );
    };
    measure();

    // Follow the anchor frame by frame while it moves. The timer below is a
    // 400ms fallback, and on its own it makes the toast lurch a third of a
    // second after the sidebar has finished opening — the toast is the one
    // element on screen not moving with everything else.
    //
    // A CSS transition would be the obvious fix and is the wrong one: it makes
    // the toast *trail* the sidebar by its own duration instead of moving with
    // it. `ResizeObserver` fires on every frame the composer is resized, which
    // is exactly what the sidebar opening does to it, so the toast tracks it
    // instead of chasing it.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (observer) {
      const el = document.querySelector(anchorSelector);
      if (el) observer.observe(el);
    }

    const timer = setProductInterval(measure, ANCHOR_REMEASURE_MS);
    window.addEventListener("resize", measure);
    return () => {
      clearProductInterval(timer);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorSelector]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
    // Only a real dismissal — the X, or the auto-hide timer running out. A
    // caller retracting its own toast via `closeFpToast` does not come through
    // here, which is the distinction that makes this worth reporting at all.
    const notify = dismissHandler.current;
    dismissHandler.current = null;
    if (notify) notify(reason);
  };

  return (
    <div>
      {toastSeverity && (
        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={open}
          autoHideDuration={toastDuration}
          onClose={handleClose}
          /* Inline rather than `sx`: MUI's own anchorOrigin styling sets
             `left: 50%` on the root and wins against an sx-generated class, so
             the toast stayed viewport-centred. An inline style beats both. */
          style={
            anchorRect === null
              ? undefined
              : {
                  left: `${anchorRect.centerX}px`,
                  right: "auto",
                  transform: "translateX(-50%)",
                  // Above a bottom-pinned composer; otherwise leave MUI's
                  // window-top placement alone and borrow only the centring.
                  // `top: auto` is what releases that placement.
                  ...(anchorRect.above
                    ? {
                        top: "auto",
                        bottom: `${Math.max(
                          ANCHOR_GAP_PX,
                          window.innerHeight - anchorRect.top + ANCHOR_GAP_PX
                        )}px`,
                      }
                    : {}),
                }
          }
        >
          {/* The close button belongs on the Alert, not on the Snackbar's
              `action` prop: MUI ignores `message`/`action` entirely when the
              Snackbar has children, so the X that used to be declared there
              never rendered and no toast could be dismissed by hand. */}
          {/* The severity icon is centred against the message; the close button
              is not. MUI top-aligns both, which reads as broken on a wrapped
              message — the icon floats beside the first line with a gap under
              it. The X is the opposite case: it belongs in the corner, where a
              dismiss control is looked for, rather than drifting down the box
              as the message gets taller (#11221). */}
          <Alert
            severity={toastSeverity}
            onClose={handleClose}
            sx={{
              alignItems: "center",
              "& .MuiAlert-icon": { alignItems: "center", py: 0 },
              "& .MuiAlert-action": { alignSelf: "flex-start", pt: 0 },
            }}
          >
            {toastTitle && <AlertTitle>{toastTitle}</AlertTitle>}
            {toastMessage}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default FpToast;
